import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { useProductionDeclarationAction } from '@/features/production-declaration/hooks/use-production-declaration-action';
import { useProductionDeclarationEmailDialog } from '@/features/production-declaration/hooks/use-production-declaration-email-dialog';
import { ProductionDeclarationSendEmailDialog } from '@/features/production-declaration/production-declaration-send-email-dialog';
import type { ProductionDeclarationMaterial } from '@/types/core/purchase-order/production-declaration-material';
import type { SupplierContact } from '@/types/core/purchase-planning/supplier';

type ProductionDeclarationContentActionProps = {
  productionDeclarationId: string;
  token?: string;
  purchaseOrderId: number;
  purchaseOrderName?: string | null;
  statusCode: string;
  declarationToken: string;
  supplierContacts?: SupplierContact[];
  supplierName?: string | null;
  materials?: ProductionDeclarationMaterial[];
};

export function ProductionDeclarationContentAction({
  productionDeclarationId,
  token,
  purchaseOrderId,
  purchaseOrderName,
  statusCode,
  declarationToken,
  supplierContacts,
  supplierName,
  materials = [],
}: ProductionDeclarationContentActionProps) {
  const { t } = useTranslation('productionDeclaration');

  const { visibility, hasAnyAction, handleApprove, handleRefuse, isPending } =
    useProductionDeclarationAction({
      statusCode,
      productionDeclarationId,
      token,
      materials,
    });

  const {
    openEmailDialog,
    setOpenEmailDialog,
    currentEmailAction,
    recipients,
    setRecipients,
    message,
    setMessage,
    initialRecipientLabels,
    handleOpenEmailDialog,
    handleSendEmailDialog,
  } = useProductionDeclarationEmailDialog({
    productionDeclarationId,
    purchaseOrderId,
    purchaseOrderName,
    declarationToken,
    onApprove: handleApprove,
    onRefuse: handleRefuse,
    supplierContacts,
    supplierName,
  });

  return (
    <>
      {hasAnyAction ? (
        <div className='flex flex-wrap gap-3'>
          {visibility.showApprove ? (
            <Button
              disabled={isPending}
              onClick={() => handleOpenEmailDialog('approve')}
              type='button'
              variant='declaration-primary'
            >
              {t('actions.approve')}
            </Button>
          ) : null}
          {visibility.showRefuse ? (
            <Button
              disabled={isPending}
              onClick={() => handleOpenEmailDialog('refuse')}
              type='button'
              variant='declaration-primary'
            >
              {t('actions.refuse')}
            </Button>
          ) : null}
        </div>
      ) : null}
      <ProductionDeclarationSendEmailDialog
        action={currentEmailAction}
        isSending={isPending}
        message={message}
        onMessageChange={setMessage}
        onOpenChange={setOpenEmailDialog}
        onRecipientsChange={setRecipients}
        onSend={handleSendEmailDialog}
        open={openEmailDialog}
        recipientLabels={initialRecipientLabels}
        recipients={recipients}
      />
    </>
  );
}
